export type MatchStatusVariant = 'finished' | 'live';

export interface MatchStatusDisplay {
  label: string;
  hasStarted: boolean;
  isFinished: boolean;
  isLive: boolean;
  statusVariant: MatchStatusVariant;
}

const FINISHED_STATUS_LABELS: Record<string, string> = {
  played: 'FT',
  finished: 'FT',
  complete: 'FT',
  completed: 'FT',
  ft: 'FT',
  fulltime: 'FT',
  aet: 'AET',
  afterextratime: 'AET',
  extratimeplayed: 'AET',
  pen: 'PENS',
  pens: 'PENS',
  penalties: 'PENS',
  penaltyshootout: 'PENS',
  shootout: 'PENS',
  afterpenalties: 'PENS',
};

const LIVE_STATUS_LABELS: Record<string, string> = {
  playing: 'LIVE',
  live: 'LIVE',
  inplay: 'LIVE',
  firsthalf: '1H',
  secondhalf: '2H',
  ht: 'HT',
  halftime: 'HT',
  extratime: 'ET',
};

const FIXTURE_STATUS_LABELS: Record<string, string> = {
  fixture: 'FIX',
  scheduled: 'FIX',
  cancelled: 'CANC',
  postponed: 'PP',
  suspended: 'SUSP',
};

export function normalizeMatchStatusKey(status: string | null | undefined): string {
  return (status ?? 'fixture')
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, '');
}

export function getMatchStatusDisplay(
  status: string | null | undefined,
  hasScore = false
): MatchStatusDisplay {
  const normalizedStatus = normalizeMatchStatusKey(status);
  const rawLabel = (status ?? '').trim();
  const finishedLabel = FINISHED_STATUS_LABELS[normalizedStatus];

  if (finishedLabel) {
    return {
      label: finishedLabel,
      hasStarted: true,
      isFinished: true,
      isLive: false,
      statusVariant: 'finished',
    };
  }

  const liveLabel = LIVE_STATUS_LABELS[normalizedStatus];
  const fixtureLabel = FIXTURE_STATUS_LABELS[normalizedStatus];

  if (fixtureLabel) {
    return {
      label: fixtureLabel,
      hasStarted: false,
      isFinished: false,
      isLive: false,
      statusVariant: 'live',
    };
  }

  if (liveLabel || hasScore) {
    return {
      label: liveLabel ?? (rawLabel ? rawLabel.toUpperCase() : 'LIVE'),
      hasStarted: true,
      isFinished: false,
      isLive: true,
      statusVariant: 'live',
    };
  }

  return {
    label: rawLabel ? rawLabel.toUpperCase() : 'FIX',
    hasStarted: false,
    isFinished: false,
    isLive: false,
    statusVariant: 'live',
  };
}
